import { Struct } from 'contexts/structs/types/types'
import { adsSelectors } from 'ducks/ads'
import { logBannerAdClick, logBannerAdImpression } from 'elements/ads/adAnalytics'
import { AdPlacementCode, getAdIntensityForPlacement } from 'elements/ads/AdPlacements'
import AdServer from 'elements/ads/AdServer'
import useAdStructs, { BannerConfig } from 'elements/ads/useAdStructs'
import {
  BILLBOARD_CAROUSEL_HEIGHT,
  BILLBOARD_CAROUSEL_WIDTH,
  useBillboardCarouselSlot,
} from 'elements/ads/useBillboardCarouselSlot'
import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useInView } from 'react-intersection-observer'
import { useSelector } from 'react-redux'
import { useFeatureFlag } from 'util/featureFlags'
/*
  display: 'block' is required to remove extra padding around iframes
  */
const ALL_BANNERS = {
  overflow: 'hidden',
}
export const BANNER_STYLES = {
  triple_widescreen: { ...ALL_BANNERS, width: 278, height: 400 },
  leaderboard: { ...ALL_BANNERS, width: 728, height: 90 },
  billboard_carousel: { ...ALL_BANNERS, width: BILLBOARD_CAROUSEL_WIDTH, height: BILLBOARD_CAROUSEL_HEIGHT },
  mobileLeaderboard: { ...ALL_BANNERS, width: 325, height: 50 },
}

const getRandomString = () => Math.floor(Math.random() * 10000000)

export const AdIframe = ({ bannerConfig, placement, rounded }) => {
  const elementIdentifier = 'ad_iframe_' + getRandomString()
  const bannerStyle = BANNER_STYLES[bannerConfig.format] || {}
  const iframeWidth = typeof bannerStyle.width === 'number' ? `${bannerStyle.width}px` : bannerStyle.width
  const isBillboardCarousel = bannerConfig.format === 'billboard_carousel'
  const { slot: billboardSlot, setContainerNode: setBillboardContainerNode } =
    useBillboardCarouselSlot(isBillboardCarousel)

  useEffect(() => {
    function iframeListener(message: MessageEvent) {
      // Messages bet sent to all ads on the page. Ensure only the corresponding iframe responds.
      // @TODO: Is accessing window.name safe for all CORS scenarios?
      if (message.data?.elementIdentifier === elementIdentifier) {
        if (message.data?.type === 'AD_CLICK') {
          handleBannerClick(bannerConfig, placement)
        }
      } else {
        // console.log(
        //   `Ignore event triggered by different iframe message.data?.elementIdentifier:${message.data?.elementIdentifier} !== elementIdentifier:${elementIdentifier}`
        // )
      }
    }

    if (bannerConfig) {
      window.addEventListener('message', iframeListener)
      return () => window.removeEventListener('message', iframeListener)
    } else {
      // no bannerConfig found, do not show any ad
    }
  }, [bannerConfig])

  const { ref: inViewRef } = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: (inView) => {
      if (inView && bannerConfig) {
        handleAdImpression(bannerConfig, placement)
      }
    },
  })

  const setBillboardContainerRef = (node: HTMLDivElement | null) => {
    setBillboardContainerNode(node)
    inViewRef(node)
  }

  // Fluid width: slot height is min(362, 362×w/1200) so narrow widths do not leave empty space above/below the creative.
  if (isBillboardCarousel) {
    return (
      <div
        ref={setBillboardContainerRef}
        style={{
          width: '100%',
          height: billboardSlot.heightPx,
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
          lineHeight: 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            width: BILLBOARD_CAROUSEL_WIDTH,
            height: BILLBOARD_CAROUSEL_HEIGHT,
            marginLeft: -BILLBOARD_CAROUSEL_WIDTH / 2,
            transform: `translateZ(0) scale(${billboardSlot.scale})`,
            transformOrigin: 'top center',
          }}
        >
          <iframe
            id="iframeBannerAd"
            name={elementIdentifier}
            src={bannerConfig.iframeUrl}
            title={bannerConfig.textLinkLabel || 'Advertisement'}
            width={BILLBOARD_CAROUSEL_WIDTH}
            height={BILLBOARD_CAROUSEL_HEIGHT}
            style={{
              border: 0,
              borderRadius: rounded ? 8 : undefined,
              display: 'block',
              overflow: 'hidden',
            }}
            frameBorder={0}
            scrolling="no"
          />
        </div>
      </div>
    )
  }

  return (
    <div style={{ width: '100%', textAlign: 'center' }} ref={inViewRef}>
      <iframe
        id="iframeBannerAd"
        name={elementIdentifier}
        src={bannerConfig.iframeUrl}
        width={iframeWidth}
        style={{ ...bannerStyle, border: 0, borderRadius: rounded ? 8 : undefined }}
        frameBorder={0}
        scrolling="no"
      ></iframe>
    </div>
  )
}

export const AdTextLink = ({ bannerConfig, placement }) => {
  /*
  Currently this is limited to a single line of text, any overflow will get converted to ellipsis
  */
  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: (inView) => {
      if (inView && bannerConfig) {
        handleAdImpression(bannerConfig, placement)
      }
    },
  })

  return (
    <div
      style={{
        textAlign: 'left',
        margin: '10px 10px 10px 0px',
      }}
      ref={ref}
    >
      <a
        href="#"
        onClick={(event) => {
          event.preventDefault()
          handleBannerClick(bannerConfig, placement)
          return false
        }}
        style={{
          fontSize: 14,
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          textOverflow: 'ellipsis',
          color: '#6F737E',
          display: 'block',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {bannerConfig.textLinkLabel}
      </a>
    </div>
  )
}

const handleBannerClick = (bannerConfig, placement) => {
  const clickUrl = bannerConfig?.clickUrl || ''

  if (!bannerConfig || !clickUrl) {
    console.warn('Invalid clickUrl')
    return
  }

  if (clickUrl.startsWith('http')) {
    window.open(clickUrl, '_blank')
  } else if (clickUrl) {
    window.location.href = clickUrl
  }

  logBannerAdClick({
    campaign_name: bannerConfig.campaign_name,
    creative_name: bannerConfig.creative_name,
    format: bannerConfig.format,
    placement,
  })
}

const handleAdImpression = (bannerConfig, placement) => {
  logBannerAdImpression({
    campaign_name: bannerConfig.campaign_name,
    creative_name: bannerConfig.creative_name,
    format: bannerConfig.format,
    placement,
  })
}

export const filterAvailableAds = (structs: Struct<BannerConfig>[], adFilters: BannerFilters) => {
  if (window.debugBannerAds && structs.length) console.log('Filtering banner ads: ', structs, adFilters)
  const ret = structs
    .filter((struct) => {
      const promoConfig = struct.data
      // check ad formats
      const ret = !!(
        adFilters.formats?.length > 0 &&
        (adFilters.formats?.includes(promoConfig?.format) || adFilters.formats?.includes('**'))
      )
      if (!ret && window.debugBannerAds)
        console.log('\tBanner format mismatch:', struct.key, promoConfig?.format, adFilters.formats)
      return ret
    })
    .filter((struct) => {
      const promoConfig = struct.data
      // check matching placements
      const ret = promoConfig.placements?.includes('**') || promoConfig.placements?.includes(adFilters.placement)

      if (!ret && window.debugBannerAds)
        console.log('\tBanner placement mismatch:', struct.key, promoConfig.placements, adFilters.placement)
      return ret
    })
  if (window.debugBannerAds && structs.length) console.log('Filtered banner ads: ', ret)
  return ret
}

export type BannerFilters = {
  formats: string[]
  placement: string
}

export const weightedRandom = <T extends Record<string, any>>(structs: Struct<T>[]) => {
  if (!structs.length) return undefined
  let totalWeight = structs.reduce((acc, struct) => acc + (struct.data?.weighting || 0), 0)
  let random = Math.random() * totalWeight
  return structs.find((struct) => {
    random -= struct.data?.weighting || 0
    return random <= 0
  })
}

export function weightedRandomMultiple(structs: Struct<BannerConfig>[], howMany: number) {
  let results: Struct<BannerConfig>[] = []
  let promoConfigsRemaining = structs.slice()
  for (let i = 0; i < howMany; i++) {
    let result = weightedRandom(promoConfigsRemaining)
    if (result) {
      results.push(result)
    }
    promoConfigsRemaining = promoConfigsRemaining.filter((promoConfig) => promoConfig !== result)
  }
  return results
}

type BannerAdPropTypes = {
  formats: string[] // Todo use union type
  placement: AdPlacementCode
  requiredWindowSize?: Record<string, { width?: number; height?: number }>
  bannerConfigOverride?: any
  containerStyle?: any
  rounded?: boolean
  className?: string
  render?: (content: ReactNode) => ReactNode
}

const BannerAd: React.FunctionComponent<BannerAdPropTypes> = (props) => {
  // Banner size stats: https://www.match2one.com/blog/standard-banner-sizes/
  // 300×250 Medium Rectangle 40%
  // 728×90 Leaderboard 25%
  // 320×50 Mobile Leaderboard 12%
  // 160×600 Wide Skyscraper; 12%
  const enableAdsFeatureFlag = useFeatureFlag('ads', 'on')
  const enableAds2FeatureFlag = useFeatureFlag('ads2', 'on')
  const enableAdsForOrg = useSelector(adsSelectors.getEnableAdsForOrg)
  const enableAds = enableAdsFeatureFlag && enableAdsForOrg
  const enableAds2 = enableAds2FeatureFlag && enableAdsForOrg
  const adIntensity = getAdIntensityForPlacement(props.placement)
  const maxAdIntensity = useSelector(adsSelectors.getAdIntensity)
  const promoConfigs = useAdStructs<BannerConfig>('banner-ad')
  const [bannerConfig, setBannerConfig] = useState<BannerConfig | null>(props.bannerConfigOverride || null)
  const [containerWidth, setContainerWidth] = useState(0)

  const containerRef = useRef<ResizeObserver | null>(null)
  const containerMeasureRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current?.disconnect()
    containerRef.current = null
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect
      setContainerWidth((prev) => (prev !== width ? width : prev))
    })
    observer.observe(el)
    containerRef.current = observer
  }, [])

  const measureWrapStyle = useMemo(
    () =>
      props.render != null
        ? { width: 'auto' as const, maxWidth: '100%', ...props.containerStyle }
        : { width: '100%' as const, ...props.containerStyle },
    [props.render, props.containerStyle]
  )

  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight })

  useEffect(() => {
    const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight })
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const validFormat = useMemo(() => {
    return props.formats.find((fmt) => {
      const style = BANNER_STYLES[fmt]
      if (!style) return true

      // Skip container width check when not yet measured (0 means unmeasured, not actually 0px)
      if (containerWidth > 0 && containerWidth < style.width) return false

      // [Optional] Check if the window size is sufficient for the ad
      const reqs = props.requiredWindowSize?.[fmt]
      const windowWidthOk = !reqs?.width || windowSize.width >= reqs.width
      const windowHeightOk = !reqs?.height || windowSize.height >= reqs.height
      return windowWidthOk && windowHeightOk
    })
  }, [props.formats, containerWidth, props.requiredWindowSize, windowSize])

  const adFilters: BannerFilters = { formats: props.formats, placement: props.placement }

  useEffect(() => {
    if (!bannerConfig) {
      const availableAds = filterAvailableAds(promoConfigs, adFilters)
      const adConfig = weightedRandom(availableAds)
      if (adConfig?.data) {
        setBannerConfig(adConfig.data)
      }
    }
  }) // This intentionally has no dependency array, it gets run on every render
  //TODO: rework this to use a concise dependency array

  if (!enableAds && !enableAds2) {
    return null
  } else if (adIntensity > maxAdIntensity) {
    return null
  } else if (!validFormat) {
    return <div ref={containerMeasureRef} style={{ width: '100%' }} />
  }

  if (bannerConfig?.creative_name === 'ad_server' || enableAds2) {
    return (
      <AdServer
        placement={props.placement}
        format={validFormat}
        rounded={props.rounded}
        render={(creativeContent) => {
          const content = (
            <div ref={containerMeasureRef} className={props.className} style={measureWrapStyle}>
              {creativeContent}
            </div>
          )
          return props.render ? props.render(content) : content
        }}
      />
    )
  } else if (bannerConfig?.iframeUrl) {
    const leaf = <AdIframe bannerConfig={bannerConfig} placement={props.placement} rounded={props.rounded} />
    const measureInner = (
      <div ref={containerMeasureRef} className={props.className} style={measureWrapStyle}>
        {leaf}
      </div>
    )
    return props.render ? props.render(measureInner) : measureInner
  } else if (bannerConfig?.textLinkLabel) {
    const leaf = <AdTextLink bannerConfig={bannerConfig} placement={props.placement} />
    const measureInner = (
      <div ref={containerMeasureRef} className={props.className} style={measureWrapStyle}>
        {leaf}
      </div>
    )
    return props.render ? props.render(measureInner) : measureInner
  } else {
    //console.warn('no ad shown', bannerConfig)
    return null
  }
}

export default BannerAd

declare global {
  interface Window {
    debugBannerAds: boolean
  }
}
